import dayjs from 'dayjs';
import {
  BarChart3,
  Building2,
  Calendar,
  FileText,
  ScanSearch,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type {
  ICompanyItemGroupedSupplyInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

import ExcelBtn from './ExcelBtn';
import GroupItemsTable from './Table';

type Props = {
  items: ICompanyItemGroupedSupplyInterface[] | null;
  endDate: Date | null;
  selectedCompany: ICompanyList | null;
  startDate: Date | null;
};

function GroupItemsDialog({
  items,
  endDate,
  selectedCompany,
  startDate,
}: Props) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 gap-2 px-4',
            'bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/20',
            'border-primary/30 hover:border-primary/50',
            'text-primary hover:text-primary',
            'transition-all duration-200',
          )}
        >
          <BarChart3 className="h-4 w-4" />
          إجماليات الأصناف
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          'h-[90vh] max-w-7xl gap-0 overflow-hidden p-0',
          'bg-white dark:bg-slate-900',
          'border border-slate-200 dark:border-slate-800',
        )}
        hiddenCloseIcon
      >
        <DialogHeader className="shrink-0 space-y-0 p-0">
          <div
            className={cn(
              'border-b px-6 py-4',
              'bg-slate-50 dark:bg-slate-800/50',
              'border-slate-200 dark:border-slate-700',
            )}
          >
            <div className="flex items-center justify-between">
              {/* Left Section - Title and Info */}
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'flex h-14 w-14 items-center justify-center rounded-xl',
                    'bg-gradient-to-br from-blue-500 to-blue-600 shadow-md',
                    'ring-4 ring-blue-100 dark:ring-blue-900/30',
                  )}
                >
                  <ScanSearch className="h-7 w-7 text-white" />
                </div>
                <div className="space-y-2">
                  <DialogTitle className="text-xl font-bold leading-tight text-slate-900 dark:text-slate-100">
                    تقرير إجماليات حركة أصناف الشركة
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <Badge
                        variant="soft"
                        className="h-6 gap-1.5 bg-slate-100 px-2.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                      >
                        {selectedCompany?.Name || 'جميع الشركات'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <Badge
                        variant="outline"
                        className="h-6 gap-1 border-blue-300 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-300"
                      >
                        {dayjs(startDate).format('DD/MM/YYYY')}
                      </Badge>
                      <span className="text-xs text-slate-400">إلى</span>
                      <Badge
                        variant="outline"
                        className="h-6 gap-1 border-blue-300 bg-blue-50 px-2.5 text-xs font-medium text-blue-700 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-300"
                      >
                        {dayjs(endDate).format('DD/MM/YYYY')}
                      </Badge>
                    </div>
                    {items && (
                      <Badge
                        variant="outline"
                        className="h-6 gap-1.5 border-emerald-300 bg-emerald-50 px-2.5 text-xs font-medium text-emerald-700 dark:border-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-300"
                      >
                        <FileText className="h-3 w-3" />
                        {items.length} صنف
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Section - Actions */}
              <div className="flex items-center gap-2">
                {items && (
                  <ExcelBtn
                    items={items}
                    endDate={endDate}
                    startDate={startDate}
                    selectedCompany={selectedCompany}
                  />
                )}
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-9 w-9 border-slate-300 dark:border-slate-600',
                      'hover:border-red-300 hover:bg-red-50 hover:text-red-600',
                      'dark:hover:border-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400',
                      'transition-colors duration-200',
                    )}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </DialogClose>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {items && items.length > 0 ? (
            <GroupItemsTable items={items} />
          ) : (
            <div className="flex h-full items-center justify-center p-8">
              <div className="max-w-sm space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                  <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                </div>
                <div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    لا توجد بيانات
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    لم يتم العثور على أي حركات للشركة المحددة خلال الفترة
                    الزمنية المختارة
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GroupItemsDialog;
